import React, { useMemo } from 'react';
import { Button, Card, Space, Spin, Tag, Typography, theme } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from '@hello-pangea/dnd';
import type { Lead } from '@/hooks/useLeads';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';
import { LeadKanbanCard } from './LeadKanbanCard';
import { LEAD_STATUSES, STATUS_COLORS, leadAnnualRevenue } from './leads.constants';

const { Text } = Typography;

type Props = {
  leads: Lead[];
  isLoading?: boolean;
  updatingLeadId?: string | null;
  onDragStatusChange: (leadId: string, newStatus: string) => void;
  onView: (lead: Lead) => void;
  onEdit: (lead: Lead, e?: React.MouseEvent) => void;
  onDelete: (lead: Lead, e?: React.MouseEvent) => void;
  onMoveStatus: (lead: Lead, status: string) => void;
  onAddLead: () => void;
};

export const LeadsKanbanBoard: React.FC<Props> = ({
  leads,
  isLoading,
  updatingLeadId,
  onDragStatusChange,
  onView,
  onEdit,
  onDelete,
  onMoveStatus,
  onAddLead,
}) => {
  const { token } = theme.useToken();
  const { formatCurrency } = useCurrency();
  const { resolve: resolveUser } = useUserDisplayResolver();

  const grouped = useMemo(() => {
    const g: Record<string, Lead[]> = {};
    LEAD_STATUSES.forEach((st) => {
      g[st] = leads.filter((l) => l.lead_status === st);
    });
    return g;
  }, [leads]);

  const onDragEnd = (result: DropResult) => {
    const { draggableId, destination, source } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;
    if (!LEAD_STATUSES.includes(destination.droppableId as (typeof LEAD_STATUSES)[number])) return;
    onDragStatusChange(draggableId, destination.droppableId);
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div
        style={{
          display: 'flex',
          gap: 16,
          overflowX: 'auto',
          paddingBottom: 8,
          minHeight: 560,
        }}
      >
        {LEAD_STATUSES.map((status) => {
          const statusLeads = grouped[status] || [];
          const columnValue = statusLeads.reduce((s, l) => s + leadAnnualRevenue(l), 0);
          const color = STATUS_COLORS[status];

          return (
            <div
              key={status}
              style={{
                flex: '0 0 300px',
                minWidth: 280,
                maxWidth: 320,
              }}
            >
              <Card
                title={
                  <div>
                    <Space>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: color,
                          display: 'inline-block',
                        }}
                      />
                      <Text strong>{status}</Text>
                      <Tag style={{ margin: 0 }}>{statusLeads.length}</Tag>
                    </Space>
                    {columnValue > 0 && (
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                        {formatCurrency(columnValue)}
                      </Text>
                    )}
                  </div>
                }
                style={{
                  background: token.colorFillAlter,
                  borderRadius: token.borderRadiusLG,
                  height: '100%',
                  minHeight: 520,
                }}
                styles={{
                  header: { borderBottom: `2px solid ${color}40`, minHeight: 56 },
                  body: { padding: 10, maxHeight: 'calc(100vh - 340px)', overflowY: 'auto' },
                }}
              >
                <Droppable droppableId={status}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        minHeight: 120,
                        background: snapshot.isDraggingOver ? `${color}12` : 'transparent',
                        borderRadius: token.borderRadius,
                        transition: 'background 0.2s',
                      }}
                    >
                      {statusLeads.map((lead, index) => {
                        const isDragDisabled = !!lead.is_converted;
                        return (
                          <Draggable
                            key={lead.id}
                            draggableId={lead.id}
                            index={index}
                            isDragDisabled={isDragDisabled}
                          >
                            {(dragProvided, dragSnapshot) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                style={{
                                  ...dragProvided.draggableProps.style,
                                  marginBottom: 8,
                                  boxShadow: dragSnapshot.isDragging
                                    ? token.boxShadowSecondary
                                    : undefined,
                                }}
                              >
                                <LeadKanbanCard
                                  lead={lead}
                                  ownerLabel={resolveUser(lead.assigned_to)}
                                  isUpdating={updatingLeadId === lead.id}
                                  dragHandleProps={
                                    isDragDisabled ? undefined : dragProvided.dragHandleProps
                                  }
                                  onView={() => onView(lead)}
                                  onEdit={(e) => onEdit(lead, e)}
                                  onDelete={(e) => onDelete(lead, e)}
                                  onMoveStatus={(s) => onMoveStatus(lead, s)}
                                />
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                      {statusLeads.length === 0 && (
                        <div
                          style={{
                            textAlign: 'center',
                            padding: '32px 16px',
                            color: token.colorTextSecondary,
                          }}
                        >
                          <Text type="secondary">No leads in this stage</Text>
                          <div style={{ marginTop: 12 }}>
                            <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={onAddLead}>
                              Add lead
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </Card>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
};
